import { useNavigate, useLocation } from 'react-router-dom';
import { Check, Copy, ArrowRight, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useState } from 'react';

function PaymentInstructionsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const planName = location.state?.planName || 'Pro';
  const [copiedField, setCopiedField] = useState(null);

  const planPrices = {
    'Basic': '₹999',
    'Pro': '₹2,499',
    'Premium': '₹4,999'
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="min-h-screen pt-20 bg-gray-50 px-4 py-12">
      <div className="max-w-3xl mx-auto">
        {/* Success Message */}
        <Card className="mb-8 bg-gradient-to-r from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Application Submitted Successfully!</h1>
            <p className="text-lg text-gray-700 mb-2">Thank you for choosing Travelodges {planName} Plan</p>
            <Badge className="bg-emerald-600 text-white text-lg px-6 py-2">
              Amount Due: {planPrices[planName]}/month
            </Badge>
          </CardContent>
        </Card>

        {/* Payment Instructions */}
        <Card className="mb-6">
          <CardHeader className="bg-gray-50 border-b">
            <CardTitle className="text-2xl">Complete Your Payment</CardTitle>
            <p className="text-gray-600 mt-2">Choose any of the following payment methods to activate your account</p>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Bank Transfer Details */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">1</span>
                Bank Transfer / NEFT / IMPS
              </h3>
              <div className="bg-gray-50 rounded-lg p-6 space-y-3">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-600 font-medium">Account Holder Name:</span>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-gray-900">Syed Mohammed Najmuddin</span>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => copyToClipboard('Syed Mohammed Najmuddin', 'name')}
                      className="h-8"
                    >
                      <Copy className="h-4 w-4" />
                      {copiedField === 'name' && <span className="ml-1 text-xs text-emerald-600">Copied!</span>}
                    </Button>
                  </div>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-600 font-medium">Account Number:</span>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-gray-900">50100288551182</span>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => copyToClipboard('50100288551182', 'account')}
                      className="h-8"
                    >
                      <Copy className="h-4 w-4" />
                      {copiedField === 'account' && <span className="ml-1 text-xs text-emerald-600">Copied!</span>}
                    </Button>
                  </div>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-600 font-medium">IFSC Code:</span>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-gray-900">HDFC0005104</span>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => copyToClipboard('HDFC0005104', 'ifsc')}
                      className="h-8"
                    >
                      <Copy className="h-4 w-4" />
                      {copiedField === 'ifsc' && <span className="ml-1 text-xs text-emerald-600">Copied!</span>}
                    </Button>
                  </div>
                </div>
                
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 font-medium">Bank Name:</span>
                  <span className="font-semibold text-gray-900">HDFC Bank</span>
                </div>
              </div>
            </div>

            {/* UPI Payment */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">2</span>
                UPI Payment (Fastest)
              </h3>
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-gray-600 font-medium mb-2">UPI ID:</p>
                    <p className="text-2xl font-bold text-gray-900">smnajmuddin@okhdfcbank</p>
                  </div>
                  <Button 
                    size="lg" 
                    className="bg-purple-600 hover:bg-purple-700"
                    onClick={() => copyToClipboard('smnajmuddin@okhdfcbank', 'upi')}
                  >
                    <Copy className="h-5 w-5 mr-2" />
                    {copiedField === 'upi' ? 'Copied!' : 'Copy UPI ID'}
                  </Button>
                </div>
                <p className="text-sm text-gray-600 mt-4">
                  Open any UPI app (Google Pay, PhonePe, Paytm) and enter this UPI ID to pay {planPrices[planName]}
                </p>
              </div>
            </div>

            {/* After Payment Instructions */}
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-emerald-900 mb-3 flex items-center">
                <span className="w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">3</span>
                After Payment - Send Confirmation
              </h3>
              <p className="text-gray-700 mb-4">
                Once payment is completed, please send the payment screenshot or reference number to:
              </p>
              <div className="space-y-3">
                <a 
                  href="https://wa.me/918499092521" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 p-3 bg-white rounded-lg hover:shadow-md transition-all"
                >
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                    <Phone className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">WhatsApp (Fastest)</p>
                    <p className="text-sm text-gray-600">+91 8499092521</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-400 ml-auto" />
                </a>
                
                <a 
                  href="mailto:travelodgesnetwork@gmail.com" 
                  className="flex items-center space-x-3 p-3 bg-white rounded-lg hover:shadow-md transition-all"
                >
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <Mail className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Email</p>
                    <p className="text-sm text-gray-600">travelodgesnetwork@gmail.com</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-400 ml-auto" />
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Activation Timeline */}
        <Card>
          <CardContent className="pt-6 pb-6">
            <h3 className="font-semibold text-gray-900 mb-4">What Happens Next?</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start space-x-3">
                <Check className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">We verify your payment (usually within 1-2 hours)</p>
              </div>
              <div className="flex items-start space-x-3">
                <Check className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">Your resort profile is activated on the Travelodges network</p>
              </div>
              <div className="flex items-start space-x-3">
                <Check className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">You receive login credentials via email</p>
              </div>
              <div className="flex items-start space-x-3">
                <Check className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">Start receiving guest referrals from partner resorts immediately</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Travelodges Branding Section */}
        <Card className="mt-8 bg-gradient-to-r from-emerald-600 to-emerald-700">
          <CardContent className="py-12 text-center">
            <div className="flex justify-center mb-6">
              <img 
                src="/travelodges-logo-new.jpg" 
                alt="Travelodges Logo" 
                className="h-32 w-auto object-contain bg-white rounded-xl p-4 shadow-lg"
              />
            </div>
            <div className="space-y-2">
              <h2 className="text-4xl font-bold text-white mb-2">Travelodges</h2>
              <p className="text-xl text-emerald-100 font-medium tracking-wide uppercase">
                Connecting Resorts
              </p>
              <p className="text-lg text-white/90 mt-4 max-w-2xl mx-auto">
                India's Premier Networking Platform for Independent Resorts
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Back Button */}
        <div className="mt-8 text-center">
          <Button 
            variant="outline" 
            size="lg"
            onClick={() => navigate('/')}
          >
            Return to Homepage
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PaymentInstructionsPage;
